import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import type { TransitionResult, Version } from './types';
import { DialogLayer } from '../ui/Dialog';
import { NotesInput } from '../ui/NotesInput';

/**
 * "Oznacz jako wysłaną": the quote went to the client outside the app (phone, WhatsApp, own e-mail). Freezes the
 * draft; the first send also takes "potrzebna wycena" down and moves the event to "Wycena".
 */
export function MarkSentDialog({ version, clientName, onDone, onClose, onReload }: { version: Version; clientName: string; onDone: (v: TransitionResult) => void; onClose: () => void; onReload: () => void }) {
  const [sentTo, setSentTo] = useState(clientName);
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');
  const send = useMutation({
    mutationFn: () => api<TransitionResult>(`/api/quotes/versions/${version.id}/mark-sent`, { method: 'POST', body: { sentTo, note, version: version.version } }),
    onSuccess: onDone,
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (sentTo.trim() === '') return setLocal('Wpisz, do kogo wysłano kosztorys');
    setLocal('');
    send.mutate();
  }
  const error = send.error instanceof ApiError ? send.error : null;
  // somebody changed or sent this version meanwhile: the screen is out of date, read it again
  const stale = !!error && (error.status === 409 || error.code === 'version_frozen');
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="mark-sent-title" onSubmit={submit} data-feature-id="quotes.mark_sent">
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="mark-sent-title">Oznacz v{version.number} jako wysłaną</h2>
        <p className="muted">Wersja zostanie zamrożona. Poprawki zrobisz potem w nowej wersji.</p>
        {stale && <ConflictNotice onReload={onReload} />}
        <label className="field">
          <span>Do kogo</span>
          <input value={sentTo} onChange={(e) => setSentTo(e.target.value)} autoComplete="off" autoFocus placeholder="np. Jan Kowalski, WhatsApp" />
        </label>
        <label className="field">
          <span>Notatka (nieobowiązkowo)</span>
          <NotesInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. wysłane z telefonu" />
        </label>
        {(local || (error && !stale)) && (
          <p className="alert" role="alert">
            {local || (error && (fieldIssues(error)[0]?.message ?? error.message))}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={send.isPending}>
            Oznacz jako wysłaną
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
